import { useState, useRef } from 'react'
import { ExportImport } from './components/ExportImport'
import { SyncIndicator } from './components/SyncIndicator'
import { SearchProvider } from './contexts/SearchContext'
import { CrosswordSettingsProvider } from './contexts/CrosswordSettingsContext'
import { CrosswordDebugPanel } from './components/CrosswordDebugPanel'
import { ConstructorView } from './components/ConstructorView'
import { PairExplorerView } from './components/PairExplorerView'
import { WordsView } from './components/WordsView'
import { PrintView } from './components/PrintView'
import { TabGroup } from './components/TabGroup'
import { ExploreOverlay } from './components/ExploreOverlay'
import { VersionToast } from './components/VersionToast'

type AppView = 'construct' | 'words' | 'pairs' | 'print'
const APP_VIEWS = ['construct', 'words', 'pairs', 'print'] as const

type AppOverlayMode = 'none' | 'word' | 'pair' | 'word-pair'
const DEFAULT_LETTER_COUNT = 6

function App() {
  const [appView, setAppView] = useState<AppView>('construct')
  const [letterSearch, setLetterSearch] = useState<string[]>(Array(DEFAULT_LETTER_COUNT).fill(''))
  const [overlayMode, setOverlayMode] = useState<AppOverlayMode>('none')
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleLetterChange = (index: number, value: string) => {
    const chars = value.toUpperCase().replace(/[^A-Z]/g, '')

    if (chars.length > 1) {
      const newSearch = [...letterSearch]
      for (let i = 0; i < chars.length && index + i < letterSearch.length; i++) {
        newSearch[index + i] = chars[i] || ''
      }
      setLetterSearch(newSearch)
      const nextEmpty = newSearch.findIndex((l, i) => i >= index && !l)
      const focusIndex = nextEmpty >= 0 ? nextEmpty : Math.min(index + chars.length, letterSearch.length - 1)
      const el = inputRefs.current[focusIndex]
      if (el) el.focus()
    } else {
      const newSearch = [...letterSearch]
      newSearch[index] = chars
      setLetterSearch(newSearch)
      if (chars && index < letterSearch.length - 1) {
        const el = inputRefs.current[index + 1]
        if (el) el.focus()
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !letterSearch[index] && index > 0) {
      // If at the end and this is an extra cell (beyond default), remove it
      if (index >= DEFAULT_LETTER_COUNT && index === letterSearch.length - 1) {
        setLetterSearch(prev => prev.slice(0, -1))
        const el = inputRefs.current[index - 1]
        if (el) el.focus()
        return
      }
      const el = inputRefs.current[index - 1]
      if (el) el.focus()
    } else if (e.key === 'ArrowLeft' && index > 0) {
      const el = inputRefs.current[index - 1]
      if (el) el.focus()
    } else if (e.key === 'ArrowRight' && index < letterSearch.length - 1) {
      const el = inputRefs.current[index + 1]
      if (el) el.focus()
    }
  }

  const addLetterSlot = () => {
    setLetterSearch(prev => [...prev, ''])
    // Focus the new input after it renders
    setTimeout(() => {
      const el = inputRefs.current[letterSearch.length]
      if (el) el.focus()
    }, 0)
  }

  return (
    <CrosswordSettingsProvider>
      <SearchProvider letterSearch={letterSearch}>
        <div className="h-screen flex flex-col bg-gray-100">
          <VersionToast />
          {/* Header */}
          <header className="px-5 py-3 shrink-0">
            <div className="flex items-center justify-between">
              {/* View toggle */}
              <TabGroup
                tabs={APP_VIEWS}
                value={appView}
                onChange={setAppView}
                className="bg-gray-200"
              />

              {/* Centered Search */}
              <div className="flex gap-1 items-center group/search">
                {letterSearch.map((letter, index) => {
                  const placeholders = ['S', 'E', '', 'R', 'C', 'H']
                  return (
                    <div key={index} className="relative">
                      <input
                        ref={el => { inputRefs.current[index] = el }}
                        type="text"
                        value={letter}
                        onChange={e => handleLetterChange(index, e.target.value)}
                        onKeyDown={e => handleKeyDown(index, e)}
                        className="w-9 h-9 border border-gray-400/60 rounded-md text-center font-mono text-sm uppercase bg-white/80 focus:bg-white focus:border-gray-600 transition-colors"
                      />
                      {!letter && placeholders[index] && (
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-300 text-sm font-medium">
                          {placeholders[index]}
                        </span>
                      )}
                    </div>
                  )
                })}
                <button
                  onClick={addLetterSlot}
                  className="w-9 h-9 border border-dashed border-gray-400/60 rounded-md flex items-center justify-center text-gray-300 hover:text-gray-500 hover:border-gray-600 transition-all opacity-0 group-hover/search:opacity-100"
                  title="Add letter slot"
                >
                  +
                </button>
              </div>

              <div className="flex items-center gap-2">
                <ExportImport />
                <SyncIndicator />
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-hidden px-4 pb-4">
            {appView === 'print' ? (
              <div className="h-full overflow-auto">
                <PrintView />
              </div>
            ) : appView === 'pairs' ? (
              <div className="h-full">
                <PairExplorerView />
              </div>
            ) : appView === 'words' ? (
              <div className="h-full">
                <WordsView />
              </div>
            ) : (
              <div className="h-full">
                <ConstructorView />
              </div>
            )}
          </main>
          <CrosswordDebugPanel />

          {/* Explore overlay — only used for word / word-pair modes; pair-only
              mode is handled by the inline PairDetailColumn in ConstructorView. */}
          {(overlayMode === 'word' || overlayMode === 'word-pair') && (
            <ExploreOverlay
              mode={overlayMode}
              onClose={() => setOverlayMode('none')}
              onModeChange={setOverlayMode}
            />
          )}

        </div>
      </SearchProvider>
    </CrosswordSettingsProvider>
  )
}

export default App
